'use client'

import React, { createContext, useContext, useEffect, useState } from 'react'
import { Socket } from 'socket.io-client'
import { socketManager } from '@/lib/socket'

interface SocketContextType {
  socket: Socket | null
  isConnected: boolean
}

const SocketContext = createContext<SocketContextType>({
  socket: null,
  isConnected: false
})

export const useSocket = () => {
  const context = useContext(SocketContext)
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider')
  }
  return context
}

interface SocketProviderProps {
  children: React.ReactNode
}

export function SocketProvider({ children }: SocketProviderProps) {
  const [socket, setSocket] = useState<Socket | null>(null)
  const [isConnected, setIsConnected] = useState<boolean>(false)

  useEffect(() => {
    console.log('🔌 SocketProvider initializing...')
    const socketInstance = socketManager.connect()
    setSocket(socketInstance)

    socketInstance.on('connect', () => {
      console.log('✅ Socket.IO connection established successfully')
      setIsConnected(true)
      
      // Make socket available globally for debugging in development
      if (process.env.NODE_ENV === 'development') {
        (window as any).socket = socketInstance
        console.log('🔧 Socket instance available as window.socket for debugging')
      }
    })

    socketInstance.on('disconnect', (reason) => {
      console.log('❌ Disconnected from Socket.IO server. Reason:', reason)
      setIsConnected(false)
      
      // Auto-reconnect after 2 seconds
      setTimeout(() => {
        if (!socketInstance.connected) {
          console.log('🔄 Attempting to reconnect...')
          socketInstance.connect()
        }
      }, 2000)
    })

    socketInstance.on('connect_error', (error) => {
      console.error('❌ Socket.IO connection error:', error)
      setIsConnected(false)
    })

    // Force connection if not connected after 1 second
    setTimeout(() => {
      if (!socketInstance.connected) {
        console.log('🔄 Forcing connection...')
        socketInstance.connect()
      }
    }, 1000)

    // Test connection status
    setTimeout(() => {
      console.log('🔍 Socket status check - Connected:', socketInstance.connected, 'ID:', socketInstance.id)
    }, 3000)

    return () => {
      console.log('🔌 SocketProvider cleanup')
      socketManager.disconnect()
      setSocket(null)
      setIsConnected(false)
    }
  }, [])

  return (
    <SocketContext.Provider value={{ socket, isConnected }}>
      {children}
    </SocketContext.Provider>
  )
}